import { ReactComponent as DownIcon } from 'assets/icons/chevronDown.svg'
import { ReactComponent as UpIcon } from 'assets/icons/chevronUp.svg'
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg'
import cx from 'classnames'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { InlineFormatButton } from 'components/InlineFormatBar/components/InlineFormatButton/InlineFormatButton'
import React, { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'
import Styles from './SearchDropdown.module.scss'

interface SearchDropdownProps<T> {
  tooltipLabel: string
  options: ReadonlyArray<T>
  currentSelection: T
  handleSelection: (option: T) => void
  itemValue: (option: T) => string
  itemLabel: (option: T) => string
  filterLogic: (lowerCaseSearchStr: string) => (options: T) => boolean
  itemRender?: (option: T) => React.ReactNode
  hideCheckmark?: boolean
  variant?: 'theme' | 'language'
  onOpenStateChange?: (isOpen: boolean) => void
}

// ABOUT: DropdownInput - This binds to events not exposed by DropdownInput
export const SearchDropdown = <T,>({
  tooltipLabel,
  options,
  currentSelection,
  handleSelection,
  itemLabel,
  itemValue,
  itemRender,
  filterLogic,
  hideCheckmark = false,
  variant,
  onOpenStateChange,
}: SearchDropdownProps<T>) => {
  const firstOptionRef = useRef<HTMLButtonElement>(null)
  const searchBoxRef = useRef<HTMLInputElement | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  // Disable up/down arrows from scrolling around as they want
  useEffect(() => {
    if (!isOpen) return

    const handleKeydown = (event: globalThis.KeyboardEvent) => {
      if (event.key.match(/ArrowUp|ArrowDown/)) {
        event.preventDefault()
      }
    }

    window.addEventListener('keydown', handleKeydown)

    return () => {
      window.removeEventListener('keydown', handleKeydown)
    }
  }, [isOpen])

  // Make up/down arrows navigate the options
  const handleArrows = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown') {
      ;((event.target as any).parentNode?.nextSibling?.firstChild || firstOptionRef.current)?.focus()
      event.nativeEvent.stopImmediatePropagation()
      event.preventDefault()
      event.stopPropagation()
      return false
    }
    if (event.key === 'ArrowUp') {
      ;((event.target as any).parentNode?.previousSibling?.firstChild || searchBoxRef.current)?.focus()
      event.nativeEvent.stopImmediatePropagation()
      event.preventDefault()
      event.stopPropagation()
      return false
    }
  }

  // Make sure we don't waste time re-rendering the options on every mouseover
  const optionList = useMemo(
    () => (close: () => void) =>
      options.filter(filterLogic(searchTerm.toLowerCase())).map((opt, n) => (
        <li key={itemValue(opt)}>
          <button
            {...(n === 0 ? { ref: firstOptionRef } : {})}
            className={cx(Styles.listItem, {
              [Styles.activeItem]: itemValue(opt) === itemValue(currentSelection),
              [Styles.hideCheckmark]: hideCheckmark,
            })}
            aria-label={itemLabel(opt)}
            onKeyUp={handleArrows}
            onClick={() => {
              handleSelection(opt)
              setSearchTerm('')
              close()
            }}
          >
            {itemRender ? itemRender(opt) : itemLabel(opt)}
          </button>
        </li>
      )),
    [
      handleSelection,
      options,
      currentSelection,
      searchTerm,
      filterLogic,
      itemValue,
      itemLabel,
      itemRender,
      hideCheckmark,
    ],
  )

  const selectedItemLabel = useMemo(() => {
    switch (variant) {
      case 'theme':
        return 'Theme'
      default:
      case 'language':
        const label = itemLabel(currentSelection)
        if (label === 'Plain Text') return 'Language'
        return label
    }
  }, [currentSelection, itemLabel, variant])

  return (
    <DropdownTrigger
      onOpenChange={(isOpen) => {
        setIsOpen(isOpen)
        onOpenStateChange?.(isOpen)
      }}
      placement={variant === 'language' ? 'bottom-start' : 'bottom'}
    >
      <InlineFormatButton label={tooltipLabel} className={Styles.textButton} data-testid="dropdownTriggerBtn">
        {selectedItemLabel}
        {isOpen ? <UpIcon /> : <DownIcon />}
      </InlineFormatButton>
      {(close) => (
        <div
          className={cx(Styles.dropdownMenuItemContainer, variant ? Styles[variant] : undefined)}
          data-testid="searchdropdownWrapper"
        >
          <div className={Styles.inputWrapper}>
            <input
              autoFocus
              ref={searchBoxRef}
              autoComplete="off"
              value={searchTerm}
              onChange={({ target }) => setSearchTerm(target.value)}
              onKeyUp={({ key }) => {
                return key.match(/ArrowDown|Enter/) ? firstOptionRef.current?.focus() : null
              }}
              type="text"
              aria-label="Search Input"
              className={Styles.search}
              data-testid="searchdropdownSearch"
              placeholder="       Search..."
            />
            <SearchIcon className={Styles.searchIcon} fill="currentColor" />
          </div>
          <div className={Styles.scrollableWrapper} data-testid="dropdown-menu">
            <ul className={Styles.dropdownMenuItemList}>{optionList(close)}</ul>
          </div>
        </div>
      )}
    </DropdownTrigger>
  )
}
